import { useCallback, useState } from "react";
import { FiX } from "react-icons/fi";
import { catchError, of, tap, type Observable } from "rxjs";

import {
  ChipList,
  StatusBadge,
  TypeBadge,
} from "@/app/library/components/molecules";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import { Button } from "@/core/ui/atoms";
import {
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  AdminSectionTitle,
  Banner,
} from "@/core/ui/molecules";
import { DrawerTemplate } from "@/core/ui/templates";
import { ErrorResponse } from "@/core/utils/request";

type TemplateDetailDrawerProps = {
  readonly onActionSuccess?: () => void;
  readonly onArchive?: (templateId: number) => Observable<unknown>;
  readonly onClose: () => void;
  readonly onEditMetadata?: (template: APILibraryTemplateResponseType) => void;
  readonly onPublish?: (templateId: number) => Observable<unknown>;
  readonly template: APILibraryTemplateResponseType;
  readonly templates?: readonly APILibraryTemplateResponseType[];
};

export function TemplateDetailDrawer({
  onActionSuccess,
  onArchive,
  onClose,
  onEditMetadata,
  onPublish,
  template,
}: TemplateDetailDrawerProps) {
  const verticals =
    template.verticals
      ?.map((vertical) => vertical.name ?? vertical.code ?? "")
      .filter(Boolean) ?? [];
  const categories =
    template.categories
      ?.map((category) => category.name ?? "")
      .filter(Boolean) ?? [];
  const [actionError, setActionError] = useState("");
  const [actionPending, setActionPending] = useState<
    "archive" | "publish" | null
  >(null);

  const runTemplateAction = useCallback(
    (
      action: ((templateId: number) => Observable<unknown>) | undefined,
      kind: "archive" | "publish",
    ) => {
      if (!template.id) {
        setActionError("Template id is required.");
        return;
      }
      if (!action) {
        return;
      }

      setActionError("");
      setActionPending(kind);
      action(template.id)
        .pipe(
          catchError((requestError) => {
            setActionError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : requestError instanceof Error
                  ? requestError.message
                  : `Unable to ${kind} template.`,
            );
            return of(null);
          }),
          tap((response) => {
            setActionPending(null);
            if (response) {
              onActionSuccess?.();
            }
          }),
        )
        .subscribe();
    },
    [onActionSuccess, template.id],
  );

  return (
    <DrawerTemplate
      content={
        <div>
          <Banner tone="warning">
            <strong>Catalog only.</strong> This template is used through packs,
            not directly assigned to plans.
          </Banner>
          {actionError ? (
            <Banner tone="error">
              <strong>Action failed:</strong> {actionError}
            </Banner>
          ) : null}
          <AdminMiniList>
            <AdminMiniRow>
              <strong>Type</strong>
              <TypeBadge type={template.type} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Verticals</strong>
              <ChipList values={verticals} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Category</strong>
              <span>{categories[0] ?? "-"}</span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Status</strong>
              <StatusBadge status={template.status} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Used by Packs</strong>
              <ChipList values={template.packs} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Fields / Items</strong>
              <span>{template.fields_count ?? template.shots_count ?? 0}</span>
            </AdminMiniRow>
          </AdminMiniList>

          <AdminSectionTitle className="mt-5">Preview</AdminSectionTitle>
          <AdminPanel className="p-[18px]">
            <p className="text-xs leading-5 text-ink-muted">
              Preview is functional for demo: metadata, status and pack usage
              are shown here. Full Form Builder / Model Builder is not
              duplicated in Super Admin.
            </p>
          </AdminPanel>
        </div>
      }
      footer={
        onArchive || onEditMetadata || onPublish ? (
          <>
            <Button
              disabled={!!actionPending || !onEditMetadata}
              onClick={() => onEditMetadata?.(template)}
            >
              Edit Metadata
            </Button>
            {template.status === "published" ? (
              <Button
                disabled={!!actionPending || !onArchive}
                onClick={() => runTemplateAction(onArchive, "archive")}
                variant="primary"
              >
                {actionPending === "archive" ? "Archiving..." : "Archive"}
              </Button>
            ) : (
              <Button
                disabled={
                  !!actionPending ||
                  !onPublish ||
                  template.status === "archived"
                }
                onClick={() => runTemplateAction(onPublish, "publish")}
                variant="primary"
              >
                {actionPending === "publish" ? "Publishing..." : "Publish"}
              </Button>
            )}
          </>
        ) : null
      }
      header={
        <div className="flex w-full items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold leading-snug text-ink">
              {template.name}
            </h2>
            <p className="mt-1 text-[13px] text-ink-muted">
              {toTitle(template.type ?? "Template")} Template · v
              {template.version ?? "-"}
            </p>
          </div>
          <button
            aria-label="Close drawer"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control border-0 bg-transparent text-ink-subtle transition-colors hover:bg-gray-100 hover:text-ink"
            onClick={onClose}
            type="button"
          >
            <FiX aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      }
    />
  );
}

function toTitle(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
